import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { setSession } from '../admin/sessionSlice';
import { setBackButtonUrl, setCompanyLogoUrl } from './uiSlice';

const isTruthyQueryValue = (value) => {
  const normalized = String(value || '').trim().toLowerCase();
  return normalized === 'true' || normalized === '1' || normalized === 'yes';
};

const uuid4 = () => {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  };

/** Guest vs org login APIs may use different success shapes; only the org path used to send exact "LOGIN SUCCESS". */
const normalizeLoginMessage = (msg) =>
  String(msg ?? '')
    .trim()
    .toUpperCase()
    .replace(/_/g, ' ')
    .replace(/\s+/g, ' ');

const hasMeaningfulErrorField = (payload) => {
  const e = payload?.error;
  if (e == null || e === false) return false;
  if (typeof e === 'string') return e.trim().length > 0;
  if (typeof e === 'object') return Object.keys(e).length > 0;
  return Boolean(e);
};

/**
 * Login responses vary: `data`, `result`, `user`, flat root, or nested `result.data`.
 */
const extractLoginUserRecord = (payload) => {
  if (!payload || typeof payload !== 'object') return null;
  if (payload.success === false) return null;
  const nested = [
    payload.data,
    payload.result,
    payload.user,
    payload.body,
    payload.content,
    payload.payload,
    payload.result?.data,
    payload.result?.user,
  ];
  for (const block of nested) {
    if (block == null) continue;
    const candidates = Array.isArray(block) ? block : [block];
    for (const item of candidates) {
      if (!item || typeof item !== 'object') continue;
      const sid = sessionIdFrom(item);
      if (sid != null && String(sid).trim() !== '') return item;
    }
  }
  const rootSid = sessionIdFrom(payload);
  if (rootSid != null && String(rootSid).trim() !== '') return payload;
  return null;
};

const sessionIdFrom = (rec) =>
  rec?.sessionId ??
  rec?.session_id ??
  rec?.sessionID ??
  rec?.sid;

const organizationIdFrom = (rec) =>
  rec?.organizationId ??
  rec?.organization_id ??
  rec?.orgId ??
  rec?.organizationID ??
  rec?.org_id;

const lobbyFrom = (rec) => rec?.lobby ?? rec?.lobbyId ?? rec?.lobby_id ?? null;

const isLoginApiSuccess = (payload) => {
  if (!payload || typeof payload !== 'object') return false;
  if (payload.success === false) return false;
  if (hasMeaningfulErrorField(payload)) return false;
  const n = normalizeLoginMessage(payload.message);
  if (n === 'LOGIN SUCCESS' || n === 'SUCCESS' || n === 'OK') return true;
  if (payload.success === true) return true;
  const st = String(payload.status ?? '').toLowerCase();
  if (st === 'success' || st === 'ok') return true;
  const rec = extractLoginUserRecord(payload);
  if (!rec) return false;
  const sid = sessionIdFrom(rec);
  const oid = organizationIdFrom(rec);
  if (sid != null && String(sid).trim() !== '' && oid != null && String(oid).trim() !== '') {
    if (n && /FAIL|ERROR|INVALID|DENIED|EXPIRED/i.test(n)) return false;
    return true;
  }
  return false;
};

export const loginUser = createAsyncThunk(
  'auth/loginUser',
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const queryParams = new URLSearchParams(window.location.search);
      const save = queryParams.get('save');
      const demo = queryParams.get('demo');
      const fromMobileApp =
        isTruthyQueryValue(queryParams.get('fromMobileApp')) ||
        sessionStorage.getItem('fromMobileApp') === 'true';
      const hasLobbyParam =
        String(queryParams.get('lobby') || '').trim().length > 0 ||
        sessionStorage.getItem('hasLobbyParam') === 'true';

      if (save) {
        const stored = localStorage.getItem('userData');
        if (!stored) return rejectWithValue('No saved user data found.');
        const userData = JSON.parse(stored);
        const hydratedUserData = {
          ...userData,
          fromMobileApp: Boolean(fromMobileApp),
          hasLobbyParam: Boolean(hasLobbyParam),
        };
        dispatch(setSession({ sessionId: hydratedUserData.sessionId, organizationId: hydratedUserData.organizationId }));
        // Ensure header UI is populated on "save" login flow too.
        dispatch(setCompanyLogoUrl(hydratedUserData.companyLogoUrl || process.env.REACT_APP_EM_LOGO || null));
        dispatch(setBackButtonUrl(hydratedUserData.backButtonRedirect || null));
        localStorage.setItem('userData', JSON.stringify(hydratedUserData));
        return hydratedUserData;
      }

      let userResponseData;
      let authSource = 'ORG_USER';
      if (demo) {
        console.log("working with demo now");
        function generateRandomUser() {
          const randomString = 'demo' + Math.floor(Math.random() * 10000);
          return {
            data: {
              data: {
                id: Math.floor(Math.random() * 100000),
                email: `${randomString}@gmail.com`,
                userId: randomString,
                firstName: randomString,
                lastName: randomString,
                employeeId: `EM${Math.floor(Math.random() * 1000).toString().padStart(3, '0')}`,
                sessionId: 'demo'+Math.floor(Math.random() * 100000),
                organizationId: 'demo'+Math.floor(Math.random() * 100000),
                gameId: null,
                role: "demobypass",
                companyLogoUrl: process.env.REACT_APP_EM_LOGO,
                backButtonRedirect: process.env.REACT_APP_BASE_URL,
              }
            }
          };
        }
        const response = generateRandomUser();
        userResponseData = response.data.data;
      } else {
        const otp = queryParams.get('otp');
        const role = String(queryParams.get('role') || '')
          .trim()
          .toUpperCase();
        if (!otp) return rejectWithValue('OTP is required for login.');

        let apiUrl = process.env.REACT_APP_FIREBASE_LOGIN_API;
        const requestData = { otp };
        if (role === 'GUEST_USER') {
          apiUrl = process.env.REACT_APP_FIREBASE_GUEST_USER_API;
          requestData.role = 'GUEST_USER';
          authSource = 'GUEST_USER';
        }

        const clientSecret =
          process.env.REACT_APP_FIREBASE_CLIENT_SECRET ||
          process.env.REACT_APP_CLIENT_SECRET;
        const headers = {
          'client-secret': clientSecret,
          'Content-Type': 'application/json',
          'Idempotency-Key': uuid4(),
        };

        const response = await axios.post(apiUrl, requestData, { headers });
        if (!isLoginApiSuccess(response.data)) {
          const hint =
            (typeof response.data?.message === 'string' && response.data.message) ||
            (typeof response.data?.error === 'string' && response.data.error);
          return rejectWithValue(hint || 'Login failed');
        }
        const raw = extractLoginUserRecord(response.data);
        if (!raw || typeof raw !== 'object') {
          return rejectWithValue('Login failed');
        }
        userResponseData = {
          ...raw,
          sessionId: sessionIdFrom(raw),
          organizationId: organizationIdFrom(raw),
          userId: raw.userId ?? raw.user_id ?? raw.id,
          firstName: raw.firstName ?? raw.first_name,
          lastName: raw.lastName ?? raw.last_name,
          gameId: raw.gameId ?? raw.game_id,
        };
        userResponseData.role = authSource;
      }

      if (
        !demo &&
        (userResponseData.sessionId == null ||
          String(userResponseData.sessionId).trim() === '' ||
          userResponseData.organizationId == null ||
          String(userResponseData.organizationId).trim() === '')
      ) {
        return rejectWithValue('Incomplete session from login service');
      }

      dispatch(setSession({ sessionId: userResponseData.sessionId, organizationId: userResponseData.organizationId }));

      // Build user data to persist
      const resolvedUserId = userResponseData.userId || userResponseData.id;
      const userData = {
        userId: resolvedUserId,
        userid: resolvedUserId,
        token: userResponseData.token || "",
        name: `${userResponseData.firstName} ${userResponseData.lastName}`.trim(),
        email: userResponseData.email || userResponseData.employeeId,
        sessionId: userResponseData.sessionId,
        organizationId: userResponseData.organizationId,
        gameId: userResponseData.gameId,
        source: demo ? 'DEMO' : authSource,
        expiry: Date.now() + 24 * 60 * 60 * 1000,
        role: userResponseData.role,
        user_type: userResponseData.role,
        companyLogoUrl: process.env.REACT_APP_EM_LOGO,
        backButtonRedirect: process.env.REACT_APP_BASE_URL,
        fromMobileApp,
        hasLobbyParam,
        lobby: lobbyFrom(userResponseData),
      };

      // Fetch organization details if not demo (guest/org); failure must not block login.
      if (!demo) {
        try {
          const orgRes = await axios.get(
            `${process.env.REACT_APP_ORG_API}/${userResponseData.organizationId}`
          );
          if (orgRes.data?.message === 'ORGANIZATIONS_FETCHED_SUCCESSFULLY') {
            if (!orgRes.data?.organization?.companyLogo) {
              userData.companyLogoUrl = process.env.REACT_APP_EM_LOGO;
            } else {
              const s3Source = process.env.REACT_APP_S3_SOURCE || "";
              const base = s3Source.endsWith("/") ? s3Source : `${s3Source}/`;
              userData.companyLogoUrl = `${base}${orgRes.data.organization.companyLogo}`;
            }
            userData.backButtonRedirect = `${process.env.REACT_APP_BASE_URL}/game-detail/${userResponseData.gameId}`;
          }
        } catch (e) {
          console.warn("login: organization fetch skipped", e?.message || e);
        }
      }
      
      dispatch(setCompanyLogoUrl(userData.companyLogoUrl));
      dispatch(setBackButtonUrl(userData.backButtonRedirect || null));
      localStorage.setItem('userData', JSON.stringify(userData));
      return userData;
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.removeItem('userData');
      }
      const data = error.response?.data;
      const apiMsg =
        (typeof data?.message === 'string' && data.message) ||
        data?.err?.message;
      return rejectWithValue(apiMsg || error.message);
    }
  }
);
const persisted = localStorage.getItem('userData');
const initialUser = persisted ? JSON.parse(persisted) : null;

const authSlice = createSlice({
  name: 'auth',
  initialState: {
    user: initialUser,         // <-- hydrate from storage
    status: initialUser ? 'succeeded' : 'idle',
    error: null
  },
  reducers: {
    logout: state => {
      state.user = null;
      localStorage.removeItem('userData');
    }
  },
  extraReducers: builder => {
    builder
      .addCase(loginUser.pending, state => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.user = action.payload;
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
        state.user = null;
      });
  }
});

export const { logout } = authSlice.actions;
export default authSlice.reducer;